import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import { ConfirmProvider } from "./lib/confirm";
import { OfflineProvider } from "./lib/offline";
import { useFeatureFlags } from "./lib/featureFlags";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import MarketingConfirmed from "./pages/MarketingConfirmed";
import DeletionRevoke from "./pages/DeletionRevoke";
import Layout from "./components/Layout";
import { PropertyProvider } from "./lib/PropertyContext";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerBooking from "./pages/CustomerBooking";
import CustomerChat from "./pages/CustomerChat";
import CustomerRequests from "./pages/CustomerRequests";
import CustomerRequestNew from "./pages/CustomerRequestNew";
import CustomerCatalogue from "./pages/CustomerCatalogue";
import CustomerUpdates from "./pages/CustomerUpdates";
import CustomerFamily from "./pages/CustomerFamily";
import CustomerHandbook from "./pages/CustomerHandbook";
import CustomerSettings from "./pages/CustomerSettings";
import CustomerArrival from "./pages/CustomerArrival";
import CustomerSupport from "./pages/CustomerSupport";
import CustomerQuotes from "./pages/CustomerQuotes";
import CustomerServicePackages from "./pages/CustomerServicePackages";
import CustomerProfileSetup from "./pages/CustomerProfileSetup";
import CustomerRatings from "./pages/CustomerRatings";
import CustomerDocuments from "./pages/CustomerDocuments";
import CustomerInvoices from "./pages/CustomerInvoices";
import TeamDocuments from "./pages/TeamDocuments";
import TeamInvoices from "./pages/TeamInvoices";
import TeamInvoiceArchive from "./pages/TeamInvoiceArchive";
import TeamDeletionAudit from "./pages/TeamDeletionAudit";
import TeamProfile from "./pages/TeamProfile";
import TeamTelegramSettings from "./pages/TeamTelegramSettings";
import TeamChecklists from "./pages/TeamChecklists";
import TeamServicePackages from "./pages/TeamServicePackages";
import Contact from "./pages/Contact";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import TeamDashboard from "./pages/TeamDashboard";
import TeamCalendar from "./pages/TeamCalendar";
import TeamCustomers from "./pages/TeamCustomers";
import TeamCustomerDetail from "./pages/TeamCustomerDetail";
import TeamPartnerDetail from "./pages/TeamPartnerDetail";
import TeamDesignGallery from "./pages/TeamDesignGallery";
import TeamImpersonations from "./pages/TeamImpersonations";
import TeamLiveStatusSchema from "./pages/TeamLiveStatusSchema";
import TeamConciergeSelect from "./pages/TeamConciergeSelect";
import ImpersonateBridge from "./pages/ImpersonateBridge";
import TeamProperties from "./pages/TeamProperties";
import TeamPropertyDetail from "./pages/TeamPropertyDetail";
import TeamPropertiesRemoved from "./pages/TeamPropertiesRemoved";
import TeamBookings from "./pages/TeamBookings";
import TeamAllowed from "./pages/TeamAllowed";
import TeamAutomatedMessages from "./pages/TeamAutomatedMessages";
import TeamChecklistMaster from "./pages/TeamChecklistMaster";
import TeamAdminPin from "./pages/TeamAdminPin";
import TeamInvoiceTemplate from "./pages/TeamInvoiceTemplate";
import TeamConciergeHistory from "./pages/TeamConciergeHistory";
import TeamPinAudit from "./pages/TeamPinAudit";
import TeamMigrationReview from "./pages/TeamMigrationReview";
import TeamRecurringTasks from "./pages/TeamRecurringTasks";
import TeamSevdesk from "./pages/TeamSevdesk";
import CustomerSevdeskInvoices from "./pages/CustomerSevdeskInvoices";
import TeamRequests from "./pages/TeamRequests";
import TeamChat from "./pages/TeamChat";
import TeamUpdates from "./pages/TeamUpdates";
import TeamAssignments from "./pages/TeamAssignments";
import TeamFeedback from "./pages/TeamFeedback";
import TeamQuotes from "./pages/TeamQuotes";
import TeamProjects from "./pages/TeamProjects";
import TeamProjectDetail from "./pages/TeamProjectDetail";
import TeamEmails from "./pages/TeamEmails";
import PartnerAssignments from "./pages/PartnerAssignments";
import TeamBookingPrint from "./pages/TeamBookingPrint";
import SplashScreen from "./components/SplashScreen";
import "./App.css";
import { BRAND_COLORS } from "./lib/bookingStatus";

function CustomerGate({ children }) {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer" && !user.profile_completed_at) {
    return <Navigate to="/app/setup" replace />;
  }
  return children;
}

function Protected({ children, roles }) {
  const { user, checking } = useAuth();
  if (checking) return (
    <div className="min-h-screen flex items-center justify-center text-navy/60 label-caps">Weissblom</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "concierge") return <Navigate to="/team" replace />;
  if (user.role === "partner") return <Navigate to="/partner" replace />;
  return <Navigate to="/app" replace />;
}

// 20 Jun 2026 · `/app/invoices` zeigt die objektbezogenen Rechnungen nur, wenn
// der Admin sie per Feature-Flag freigegeben hat — sonst Weiterleitung auf die
// aktive sevDesk-Rechnungsansicht.
function CustomerInvoicesRoute() {
  const { propertyInvoices, ready } = useFeatureFlags();
  if (!ready) return null;
  if (!propertyInvoices) return <Navigate to="/app/sevdesk-invoices" replace />;
  return <CustomerInvoices />;
}
function App() {
  // Feb 2026 · Entfernt das statische Pre-React-Splash-Overlay (index.html),
  // sobald React gemountet ist. Der React-SplashScreen (z-9999) liegt darüber,
  // daher ist das Ausblenden nahtlos; in Sessions ohne Splash gibt der Fade
  // die App frei.
  useEffect(() => {
    const el = document.getElementById("preroot-splash");
    if (!el) return;
    el.style.transition = "opacity 280ms ease";
    el.style.opacity = "0";
    const t = setTimeout(() => el.remove(), 300);
    return () => clearTimeout(t);
  }, []);
  return (
    <AuthProvider>
      <I18nProvider>
        <ConfirmProvider>
          <OfflineProvider>
          <BrowserRouter>
          {/* Feb 2026 · 11 Sep 2026 · Splash Screen mounted OUTSIDE Routes
              so it overlays the full app during initial mount. Renders
              itself once per browser session (sessionStorage flag),
              then unmounts and reveals the underlying route.
              `showSubtitle` steuert den mehrsprachigen Impact-Text unter
              dem Logo — bei `true` wird DE/EN via `useI18n().lang`
              gerendert („Ihr Zuhause, in besten Händen." / „Your home,
              in the best hands."). Zum Aktivieren: `showSubtitle` auf
              `true` setzen. */}
          <SplashScreen />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory, border: "1px solid #C9A961", borderRadius: 2 },
            }}
          />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/marketing-confirm" element={<MarketingConfirmed />} />
            <Route path="/deletion-revoke" element={<DeletionRevoke />} />
            <Route path="/impersonate" element={<ImpersonateBridge />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/app/setup" element={<Protected roles={["customer"]}><CustomerProfileSetup /></Protected>} />

            <Route path="/app" element={<Protected roles={["customer","family","guest","admin","concierge"]}><CustomerGate><PropertyProvider><Layout view="customer" /></PropertyProvider></CustomerGate></Protected>}>
              <Route index element={<CustomerDashboard />} />
              <Route path="arrival" element={<CustomerArrival />} />
              <Route path="booking" element={<CustomerBooking />} />
              <Route path="chat" element={<CustomerChat />} />
              <Route path="requests" element={<CustomerRequests />} />
              <Route path="request-new" element={<CustomerRequestNew />} />
              <Route path="catalogue" element={<CustomerCatalogue />} />
              <Route path="updates" element={<CustomerUpdates />} />
              <Route path="handbook" element={<CustomerHandbook />} />
              <Route path="quotes" element={<CustomerQuotes />} />
              <Route path="service-packages" element={<CustomerServicePackages />} />
              <Route path="ratings" element={<CustomerRatings />} />
              <Route path="documents" element={<CustomerDocuments />} />
              <Route path="invoices" element={<CustomerInvoicesRoute />} />
              <Route path="sevdesk-invoices" element={<CustomerSevdeskInvoices />} />
              <Route path="family" element={<CustomerFamily />} />
              <Route path="settings" element={<CustomerSettings />} />
              <Route path="support" element={<CustomerSupport />} />
            </Route>

            <Route path="/team" element={<Protected roles={["admin","concierge"]}><Layout view="team" /></Protected>}>
              <Route index element={<TeamDashboard />} />
              <Route path="calendar" element={<TeamCalendar />} />
              <Route path="customers" element={<TeamCustomers />} />
              <Route path="customers/:id" element={<TeamCustomerDetail />} />
              <Route path="partners/:id" element={<TeamPartnerDetail />} />
              <Route path="properties" element={<TeamProperties />} />
              <Route path="properties/removed" element={<TeamPropertiesRemoved />} />
              <Route path="properties/:id" element={<TeamPropertyDetail />} />
              <Route path="bookings" element={<TeamBookings />} />
              <Route path="allowed" element={<TeamAllowed />} />
              <Route path="requests" element={<TeamRequests />} />
              <Route path="chat" element={<TeamChat />} />
              <Route path="updates" element={<TeamUpdates />} />
              <Route path="assignments" element={<TeamAssignments />} />
              <Route path="feedback" element={<TeamFeedback />} />
              <Route path="quotes" element={<TeamQuotes />} />
              <Route path="service-packages" element={<TeamServicePackages />} />
              <Route path="projects" element={<TeamProjects />} />
              <Route path="projects/:id" element={<TeamProjectDetail />} />
              <Route path="emails" element={<TeamEmails />} />
              <Route path="documents" element={<TeamDocuments />} />
              <Route path="invoices" element={<TeamInvoices />} />
              <Route path="invoice-archive" element={<TeamInvoiceArchive />} />
              <Route path="deletion-audit" element={<TeamDeletionAudit />} />
              <Route path="profile" element={<TeamProfile />} />
              <Route path="checklists" element={<TeamChecklists />} />
              <Route path="design-gallery" element={<TeamDesignGallery />} />
              <Route path="impersonations" element={<TeamImpersonations />} />
              <Route path="live-status-schema" element={<TeamLiveStatusSchema />} />
              <Route path="settings/automated-messages" element={<TeamAutomatedMessages />} />
              <Route path="settings/checklist-master" element={<TeamChecklistMaster />} />
              <Route path="settings/invoice-template" element={<TeamInvoiceTemplate />} />
              <Route path="settings/concierge-history" element={<TeamConciergeHistory />} />
              <Route path="settings/edit-pin" element={<TeamAdminPin />} />
              <Route path="settings/pin-audit" element={<TeamPinAudit />} />
              <Route path="settings/migration-review" element={<TeamMigrationReview />} />
              <Route path="settings/telegram" element={<TeamTelegramSettings />} />
              <Route path="recurring-tasks" element={<TeamRecurringTasks />} />
              <Route path="sevdesk" element={<TeamSevdesk />} />
              <Route path="concierge-select" element={<TeamConciergeSelect />} />
            </Route>

            <Route path="/partner" element={<Protected roles={["partner"]}><Layout view="partner" /></Protected>}>
              <Route index element={<PartnerAssignments />} />
            </Route>

            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />

            {/* Print-only routes — kept outside the Layout wrapper so the
                sidebar/header don't leak onto the printed sheet. */}
            <Route
              path="/team/print/booking/:id"
              element={<Protected roles={["admin","concierge"]}><TeamBookingPrint /></Protected>}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
          </OfflineProvider>
        </ConfirmProvider>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
